import { requester } from "App";
import { useEffect, useState } from "react";
import styles from './SalesRateManagement.module.css';

function SalesRateManagement() {
    const [isMounted, setMounted] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({
        cardCommissionRate: '',
        serverUsageFee: '',
        licenseFee: '',
        salesCommissionType: '',
        salesCommissionValue: ''
    });

    const [previewValues, setPreviewValues] = useState({
        cardCommissionRate: '',
        salesCommissionValue: ''
    });

    const tooltips = {
        cardCommissionRate: "카드수수료율 (예: 3.5% → 350 입력)",
        serverUsageFee: "서버사용료 (원 단위, 예: 50,000원 → 50000 입력)",
        licenseFee: "라이센스비용 (원 단위, 예: 100,000원 → 100000 입력)",
        salesCommission: "판매수수료 (퍼센트: 3.5% → 350 입력, 정액제: 1,000원 → 1000 입력)"
    };

    useEffect(() => {
        setMounted(true);
    }, [])

    useEffect(() => {
        // 컴포넌트 마운트 시 벤더 목록 조회
        if (isMounted) {
            let data = { storeId: 'root' }
            requester.findAllVendor(data, (result) => {
                console.log(result.data);
                setVendors(result.data);
            });
        }
    }, [isMounted]);

    const formatRate = (rate) => {
        return rate ? (rate / 100).toFixed(2) + '%' : '-';
    };
    
    const formatMoney = (amount) => {
        return amount ? `${amount.toLocaleString()}원` : '-';
    };
    
    const formatCommission = (type, value) => {
        if (!type || !value) return '-';
        
        if (type === 'PERCENTAGE') {
            return formatRate(value);
        } else {
            return formatMoney(value);
        }
    };

    const handleEdit = (vendor) => {
        setEditingId(vendor.id);
        setEditForm({
            cardCommissionRate: vendor.cardCommissionRate || '',
            serverUsageFee: vendor.serverUsageFee || '',
            licenseFee: vendor.licenseFee || '',
            salesCommissionType: vendor.salesCommissionType || 'PERCENTAGE',
            salesCommissionValue: vendor.salesCommissionValue || ''
        });
    };

    const handleSave = async (vendorId) => {
        const updateData = {
            id: vendorId,
            ...editForm
        };

        requester.updateVendor(updateData, (result) => {
            if (result.code === 0) {
                // 수정 성공 시 목록 새로고침
                setEditingId(null);
                setVendors(result.data);
                console.log(result.data);
            } else {
                alert('수정에 실패했습니다.');
            }
        });
    };

    const handleCancel = () => {
        setEditingId(null);
    };

    const handleCardRateChange = (e) => {
        const value = e.target.value;
        setEditForm({
            ...editForm,
            cardCommissionRate: value
        });
        setPreviewValues({
            ...previewValues,
            cardCommissionRate: value ? (value / 100).toFixed(2) + '%' : ''
        });
    };

    const handleSalesCommissionChange = (e) => {
        const value = e.target.value;
        setEditForm({
            ...editForm,
            salesCommissionValue: value
        });
        if (editForm.salesCommissionType === 'PERCENTAGE') {
            setPreviewValues({
                ...previewValues,
                salesCommissionValue: value ? (value / 100).toFixed(2) + '%' : ''
            });
        } else {
            setPreviewValues({
                ...previewValues,
                salesCommissionValue: value ? `${Number(value).toLocaleString()}원` : ''
            });
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>판매자 수수료 관리</h2>
            
            {/* 도움말 섹션 */}
            <div className={styles.helpSection}>
                <h3>입력 가이드</h3>
                <ul>
                    <li>카드수수료율: 3.5%를 입력하려면 350을 입력하세요 (100을 곱한 값)</li>
                    <li>서버사용료: 원 단위로 입력하세요 (예: 50,000원 → 50000)</li>
                    <li>라이센스비용: 원 단위로 입력하세요 (예: 100,000원 → 100000)</li>
                    <li>판매수수료:
                        <ul>
                            <li>퍼센트 선택 시: 3.5%를 입력하려면 350을 입력하세요</li>
                            <li>정액제 선택 시: 원 단위로 입력하세요 (예: 1,000원 → 1000)</li>
                        </ul>
                    </li>
                </ul>
            </div>

            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>판매자명</th>
                        <th>카드수수료율</th>
                        <th>서버사용료</th>
                        <th>라이센스비용</th>
                        <th>판매수수료</th>
                        <th>작업</th>
                    </tr>
                </thead>
                <tbody>
                    {vendors?.map((vendor) => (
                        <tr key={vendor.id}>
                            <td>{vendor.userName}</td>
                            {editingId === vendor.id ? (
                                <>
                                    <td>
                                        <div className={styles.inputGroup}>
                                            <input
                                                type="number"
                                                value={editForm.cardCommissionRate}
                                                onChange={handleCardRateChange}
                                                className={styles.input}
                                                placeholder="예: 3.5% → 350"
                                                title={tooltips.cardCommissionRate}
                                                min="0"
                                                max="10000"
                                            />
                                            {previewValues.cardCommissionRate && (
                                                <div className={styles.preview}>
                                                    → {previewValues.cardCommissionRate}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={editForm.serverUsageFee}
                                            onChange={(e) => setEditForm({
                                                ...editForm,
                                                serverUsageFee: e.target.value
                                            })}
                                            className={styles.input}
                                            placeholder="원 단위 입력"
                                            title={tooltips.serverUsageFee}
                                            min="0"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            value={editForm.licenseFee}
                                            onChange={(e) => setEditForm({
                                                ...editForm,
                                                licenseFee: e.target.value
                                            })}
                                            className={styles.input}
                                            placeholder="원 단위 입력"
                                            title={tooltips.licenseFee}
                                            min="0"
                                        />
                                    </td>
                                    <td>
                                        <div className={styles.inputGroup}>
                                            <select
                                                value={editForm.salesCommissionType}
                                                onChange={(e) => {
                                                    setEditForm({
                                                        ...editForm,
                                                        salesCommissionType: e.target.value,
                                                        salesCommissionValue: ''
                                                    });
                                                    setPreviewValues({
                                                        ...previewValues,
                                                        salesCommissionValue: ''
                                                    });
                                                }}
                                                className={styles.select}
                                                title="수수료 타입 선택"
                                            >
                                                <option value="PERCENTAGE">퍼센트</option>
                                                <option value="FIXED_AMOUNT">정액제</option>
                                            </select>
                                            <input
                                                type="number"
                                                value={editForm.salesCommissionValue}
                                                onChange={handleSalesCommissionChange}
                                                className={styles.input}
                                                placeholder={editForm.salesCommissionType === 'PERCENTAGE' ? '예: 3.5% → 350' : '원 단위 입력'}
                                                title={tooltips.salesCommission}
                                                min="0"
                                                max={editForm.salesCommissionType === 'PERCENTAGE' ? "10000" : undefined}
                                            />
                                            {previewValues.salesCommissionValue && (
                                                <div className={styles.preview}>
                                                    → {previewValues.salesCommissionValue}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <button onClick={() => handleSave(vendor.id)} className={styles.saveBtn}>저장</button>
                                        <button onClick={handleCancel} className={styles.cancelBtn}>취소</button>
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td>{formatRate(vendor.cardCommissionRate)}</td>
                                    <td>{formatMoney(vendor.serverUsageFee)}</td>
                                    <td>{formatMoney(vendor.licenseFee)}</td>
                                    <td>{formatCommission(vendor.salesCommissionType, vendor.salesCommissionValue)}</td>
                                    <td>
                                        <button onClick={() => handleEdit(vendor)} className={styles.editBtn}>수정</button>
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default SalesRateManagement;